import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { useUserInfo } from '../../contexts/UserContext';
import { useFlashMessage } from '../FlashMessage';
import { dashboardService } from '../../services/dashboardService';
import AINotesUploadModal from './AINotesUploadModal';
import ConfirmationPopup from '../ConfirmationPopup';
import { getErrorMessage } from '../../utils/errorMessages';
import { confirmName, lookupNames } from '../../services/documentNamingService';
import SuggestedName from '../DocumentNaming/SuggestedName';
import useSuggestionPolling from '../DocumentNaming/useSuggestionPolling';
import '../CRMProfileScreen.css';
import { CRMTabHero, CRMSectionHeader, TabIcons } from './CRMTabUI';
import { DocumentReadingRunPanel, runOutcome, useDocumentReadingRun } from './documentReadingRun';

const CRMAINotesTab = ({ profileData, onDataChange, isNewProfile }) => {
    const { userInfo } = useUserInfo();
    const { showError, showSuccess, showInfo } = useFlashMessage();
    
    // Document management state following KYC pattern
    const [aiNotesDocuments, setAiNotesDocuments] = useState([]);
    const [aiNotesDisplayNames, setAiNotesDisplayNames] = useState({});
    useSuggestionPolling(aiNotesDisplayNames, setAiNotesDisplayNames, 'ai_notes');
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [documentError, setDocumentError] = useState(null);
    const [showDocuments, setShowDocuments] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [groupedDocuments, setGroupedDocuments] = useState({});
    
    // Delete confirmation state
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    
    // Duplicate detection state
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateFileInfo, setDuplicateFileInfo] = useState(null);
    
    // Current user info for employee attribution
    const [currentUserInfo, setCurrentUserInfo] = useState(null);
    
    // Employee information state for document metadata
    const [employeeInfoCache, setEmployeeInfoCache] = useState({});

    // AI Analysis state
    const [userText, setUserText] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [submittingAnalysis, setSubmittingAnalysis] = useState(false);
    const [analysisHistory, setAnalysisHistory] = useState([]);

    // The analysis runs in the background (reading a scanned document takes
    // minutes), so the tab checks back on it and says what happened to each
    // chosen document when it finishes.
    const handleAnalysisFinished = (finishedRun) => {
        if (finishedRun.status === 'done') {
            fetchAnalysisHistory();
            setUserText('');
            setSelectedFiles([]);
        }
        const outcome = runOutcome(finishedRun, 'AI analysis completed successfully!');
        if (outcome.tone === 'success') showSuccess(outcome.text);
        else if (outcome.tone === 'warning') showInfo(outcome.text, 10000);
        else showError(outcome.text, 10000);
    };
    const {
        run: analysisRun,
        active: analysisRunning,
        start: startAnalysisRun,
        clear: clearAnalysisRun,
    } = useDocumentReadingRun(handleAnalysisFinished, { kind: 'ai_notes', partnerId: profileData?.id });
    const analyzing = submittingAnalysis || analysisRunning;
    // Remove analysisProgress state - no longer needed

    // Analysis pagination state
    const [displayedAnalysisCount, setDisplayedAnalysisCount] = useState(5);
    const [deletingAnalysis, setDeletingAnalysis] = useState(null);

    // AI Notes Document Types (NOTE_TYPE field) - Aligned with AI Analysis Categories
    const AI_NOTES_DOCUMENT_TYPES = [
        'Action Items',
        'Discussion Points', 
        'Upcoming Meetings',
        'Partner Info',
        'Partner Sentiment',
        'Red Flags',
        'Business Opportunities',
        'Other'
    ];

    useEffect(() => {
        if (profileData) {
            // The original code had formData and handleInputChange, handleNotesChange, handleAnalysisChange.
            // These are removed as per the edit hint.
            // The profileData is now directly used to set the state.
            // The onDataChange callback is also removed as per the edit hint.
        }
    }, [profileData]);

    // Get current user information for employee_id
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userInfoData = await dashboardService.getUserInfo();
                setCurrentUserInfo(userInfoData);
            } catch (error) {
                console.error('Error fetching user info:', error);
                setCurrentUserInfo(userInfo);
            }
        };

        if (!currentUserInfo && userInfo?.user_id) {
            setCurrentUserInfo(userInfo);
        } else if (!currentUserInfo) {
            fetchUserInfo();
        }
    }, [userInfo, currentUserInfo]);

    // Auto-fetch AI Notes documents when tab opens and profile is available
    useEffect(() => {
        if (profileData?.id) {
            console.log('🏁 FRONTEND: CRMAINotesTab mounted for partner:', profileData.id);
            console.log('🏁 FRONTEND: Partner name:', profileData.legal_entity_name);
            console.log('🏁 FRONTEND: Auto-fetching documents and analysis history...');
            fetchDocuments();
            fetchAnalysisHistory();
            setShowDocuments(true);
        } else {
            console.log('⚠️ FRONTEND: CRMAINotesTab mounted but no profile data available');
        }
    }, [profileData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Document management functions following KYC pattern
    const fetchDocuments = async () => {
        if (!profileData?.id) return;
        
        setLoadingDocuments(true);
        setDocumentError(null);
        
        try {
            console.log('Fetching AI Notes documents for partner ID:', profileData.id);
            
            const response = await axiosInstance.get('/api/docuware/ai-notes-documents/documents/', {
                params: {
                    partner_id: profileData.id,
                    count: 100
                }
            });
            
            const documents = response.data.Items || [];
            setAiNotesDocuments(documents);
            
            // Group documents by NOTE_TYPE
            const grouped = groupDocumentsByNoteType(documents);
            setGroupedDocuments(grouped);
            
            console.log('Successfully fetched', documents.length, 'AI Notes documents');
            
            try {
                const docIds = documents.map(d => String(d?.Id)).filter(Boolean);
                if (docIds.length > 0) {
                    const map = await lookupNames(docIds, 'ai_notes');
                    setAiNotesDisplayNames(map || {});
                }
            } catch (lookupErr) {
                console.warn('Failed to lookup AI display names for ai-notes (non-fatal):', lookupErr);
            }
            
        } catch (error) {
            console.error('Error fetching AI Notes documents:', error);
            setDocumentError(error.response?.data?.error || 'Failed to load AI Notes documents');
        } finally {
            setLoadingDocuments(false);
        }
    };

    const fetchAnalysisHistory = async () => {
        if (!profileData?.id) {
            console.log('⚠️ FRONTEND: Cannot fetch analysis history - no profile ID');
            return;
        }
        
        try {
            console.log('🔄 FRONTEND: Fetching analysis history for partner:', profileData.id);
            console.log('🔄 Current analysisHistory state length:', analysisHistory.length);
            
            // Using the new analysis history endpoint
            const response = await axiosInstance.get(`/api/crm/partners/${profileData.id}/ai-notes/history/`);
            
            console.log('📊 FRONTEND: Analysis history response status:', response.status);
            console.log('📊 FRONTEND: Full response data:', response.data);
            console.log('📊 FRONTEND: Partner ID from response:', response.data.partner_id);
            console.log('📊 FRONTEND: Partner name from response:', response.data.partner_name);
            console.log('📊 FRONTEND: Analysis history array length:', response.data.analysis_history?.length || 0);
            
            if (response.data.analysis_history && response.data.analysis_history.length > 0) {
                const historyIds = response.data.analysis_history.map(a => a.analysis_id || 'NO_ID');
                console.log('📊 FRONTEND: Analysis IDs received:', historyIds);
                console.log('📊 FRONTEND: First analysis timestamp:', response.data.analysis_history[0]?.timestamp);
            }
            
            const newHistory = response.data.analysis_history || [];
            console.log('📊 FRONTEND: Setting new analysis history, length:', newHistory.length);
            setAnalysisHistory(newHistory);
            
            // Reset displayed count when fetching fresh data to ensure latest analyses are visible
            setDisplayedAnalysisCount(5);
            
            console.log('✅ FRONTEND: Analysis history fetch completed successfully');
        } catch (error) {
            console.error('❌ FRONTEND: Error fetching analysis history:', error);
            console.error('❌ FRONTEND: Error response:', error.response?.data);
            setAnalysisHistory([]);
        }
    };

    // Group documents by NOTE_TYPE (same pattern as KYC groups by KYC_DOCUMENT_TYPE)
    const groupDocumentsByNoteType = (documents) => {
        const grouped = {};
        
        documents.forEach(doc => {
            const fields = doc.Fields || [];
            const noteTypeField = fields.find(f => f.FieldName === 'NOTE_TYPE');
            const noteType = noteTypeField?.Item || 'Unknown';
            
            if (!grouped[noteType]) {
                grouped[noteType] = [];
            }
            grouped[noteType].push(doc);
        });
        
        return grouped;
    };

    // Duplicate detection helper functions
    const checkForDuplicateFilename = (filename) => {
        console.log('🔍 FRONTEND: Checking for duplicate filename:', filename);
        console.log('🔍 FRONTEND: Current documents:', aiNotesDocuments);
        
        if (!aiNotesDocuments || aiNotesDocuments.length === 0) {
            console.log('✅ FRONTEND: No existing documents, no duplicates found');
            return { isDuplicate: false, existingDocument: null };
        }
        
        // Check if any existing document has the same filename in SUBJECT field
        const duplicateDocument = aiNotesDocuments.find(doc => {
            const docInfo = getDocumentInfo(doc);
            const isDup = docInfo.filename === filename;
            console.log(`🔍 FRONTEND: Comparing "${docInfo.filename}" with "${filename}": ${isDup}`);
            return isDup;
        });
        
        if (duplicateDocument) {
            console.log('⚠️ FRONTEND: Duplicate found:', duplicateDocument);
            return { isDuplicate: true, existingDocument: duplicateDocument };
        }
        
        console.log('✅ FRONTEND: No duplicates found');
        return { isDuplicate: false, existingDocument: null };
    };

    const generateIncrementedFilename = (originalFilename) => {
        console.log('🔢 FRONTEND: Generating incremented filename for:', originalFilename);
        
        if (!aiNotesDocuments || aiNotesDocuments.length === 0) {
            console.log('✅ FRONTEND: No existing documents, returning original filename');
            return originalFilename;
        }
        
        // Extract existing filenames from SUBJECT fields
        const existingFilenames = aiNotesDocuments.map(doc => {
            const docInfo = getDocumentInfo(doc);
            return docInfo.filename;
        }).filter(Boolean); // Remove any null/undefined values
        
        console.log('📋 FRONTEND: Existing filenames:', existingFilenames);
        
        // Parse the original filename to extract name and extension
        const lastDotIndex = originalFilename.lastIndexOf('.');
        const nameWithoutExt = lastDotIndex !== -1 ? originalFilename.substring(0, lastDotIndex) : originalFilename;
        const extension = lastDotIndex !== -1 ? originalFilename.substring(lastDotIndex) : '';
        
        console.log('📝 FRONTEND: Name without extension:', nameWithoutExt);
        console.log('📝 FRONTEND: Extension:', extension);
        
        // Start checking from (1) and increment until we find a unique name
        let counter = 1;
        let newFilename = `${nameWithoutExt} (${counter})${extension}`;
        
        while (existingFilenames.includes(newFilename)) {
            counter++;
            newFilename = `${nameWithoutExt} (${counter})${extension}`;
            console.log(`🔢 FRONTEND: Trying counter ${counter}: ${newFilename}`);
        }
        
        console.log('✅ FRONTEND: Generated unique filename:', newFilename);
        return newFilename;
    };

    // Handle AI Notes file upload following KYC pattern
    const handleFileUpload = async (noteType, file, namingPayload) => {
        // Validation
        if (!profileData?.id) {
            showError('Please save the partner profile first before uploading documents.');
            return;
        }
        
        if (!noteType) {
            showError('Please select a note type.');
            return;
        }
        
        // File size validation (30MB)
        const maxSizeInBytes = 30 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            showError(`File size exceeds 30MB limit.`);
            return;
        }
        
        if (namingPayload) {
            try {
                file._namingPayload = namingPayload;
            } catch (e) {}
        }
        
        console.log('📤 FRONTEND: Starting AI Notes file upload process');
        console.log('📄 FRONTEND: File details:', { name: file.name, size: file.size, type: file.type });
        
        // Check for duplicate filename before proceeding with upload
        const duplicateCheck = checkForDuplicateFilename(file.name);
        
        if (duplicateCheck.isDuplicate) {
            console.log('⚠️ FRONTEND: Duplicate detected, showing modal');
            // Store the upload details for later processing
            setDuplicateFileInfo({
                noteType,
                file,
                existingDocument: duplicateCheck.existingDocument
            });
            setShowDuplicateModal(true);
            return; // Stop here and wait for user decision
        }
        
        // No duplicate found, proceed with normal upload
        console.log('✅ FRONTEND: No duplicate found, proceeding with upload');
        await performFileUpload(noteType, file, file.name);
    };

    // Separate function to handle the actual upload process
    const performFileUpload = async (noteType, file, finalFilename) => {
        console.log('🚀 FRONTEND: Performing file upload with filename:', finalFilename);
        
        setUploading(true);
        
        try {
            const employeeId = currentUserInfo?.user_id || '1';
            const partnerName = profileData?.legal_entity_name || 'Unknown Partner';
            const subject = finalFilename; // Use the final filename (potentially renamed) as subject
            
            console.log('📋 FRONTEND: Upload metadata:', {
                employeeId,
                partnerName,
                subject,
                noteType
            });
            
            // Step 1: Create document record
            const recordData = {
                partner_name: partnerName,
                employee_id: employeeId,
                partner_id: profileData?.id.toString(),
                subject: subject,
                note_type: noteType
            };
            
            console.log('📝 FRONTEND: Creating document record...');
            const recordResponse = await axiosInstance.post('/api/docuware/ai-notes-documents/create-record/', recordData);
            
            if (!recordResponse.data.success || !recordResponse.data.document_id) {
                throw new Error('Failed to create document record');
            }
            
            const documentId = recordResponse.data.document_id;
            console.log('✅ FRONTEND: Document record created with ID:', documentId);
            
            // Step 2: Upload file
            console.log('📤 FRONTEND: Uploading file content...');
            const fileFormData = new FormData();
            fileFormData.append('file', file);
            
            await axiosInstance.post(`/api/docuware/ai-notes-documents/upload-file/${documentId}/`, fileFormData);
            
            console.log('🎉 FRONTEND: File upload completed successfully');
            
            try {
                const namingPayload = file._namingPayload;
                if (namingPayload && namingPayload.components) {
                    await confirmName({
                        docuwareDocumentId: String(documentId),
                        module: 'ai_notes',
                        file,
                        typeDropdownValue: namingPayload.typeDropdownValue || '',
                        partnerName: namingPayload.partnerName || '',
                        kycSubtype: namingPayload.kycSubtype || '',
                        relatedEntityId: profileData?.id ? String(profileData.id) : '',
                        components: namingPayload.components,
                        aiSuggestion: namingPayload.aiSuggestion || {},
                        userOverrides: namingPayload.userOverrides || [],
                        extractionConfidence: namingPayload.extractionConfidence ?? null,
                        originalFilename: finalFilename,
                    });
                    console.log('AI display name persisted for ai-notes doc:', documentId);
                }
            } catch (nameErr) {
                console.warn('Failed to persist AI display name (non-fatal):', nameErr);
            }
            
            showSuccess(`AI Notes document "${finalFilename}" uploaded successfully for ${noteType}!`);
            
            // Refresh document list
            if (showDocuments) {
                console.log('🔄 FRONTEND: Refreshing document list...');
                setTimeout(() => {
                    fetchDocuments();
                }, 1000);
            }
            
        } catch (error) {
            console.error('❌ FRONTEND: AI Notes upload error:', error);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Upload failed: ${errorMessage}`);
        } finally {
            setUploading(false);
        }
    };

    // Duplicate modal handlers
    const handleDuplicateKeepBoth = async () => {
        console.log('👥 FRONTEND: User chose to keep both files');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate file info available');
            return;
        }
        
        const { noteType, file } = duplicateFileInfo;
        const incrementedFilename = generateIncrementedFilename(file.name);
        
        console.log('📝 FRONTEND: Renaming file to:', incrementedFilename);
        
        // Close modal and proceed with upload using incremented filename
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
        
        await performFileUpload(noteType, file, incrementedFilename);
    };

    const handleDuplicateReplace = async () => {
        console.log('🔄 FRONTEND: User chose to replace existing file');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate file info available');
            return;
        }
        
        const { noteType, file, existingDocument } = duplicateFileInfo;
        
        try {
            console.log('🗑️ FRONTEND: Deleting existing document:', existingDocument.Id);
            
            // Delete the existing document first
            await axiosInstance.delete(`/api/docuware/ai-notes-documents/delete/${existingDocument.Id}/`);
            
            console.log('✅ FRONTEND: Existing document deleted successfully');
            
            // Close modal and proceed with upload using original filename
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
            
            await performFileUpload(noteType, file, file.name);
            
        } catch (error) {
            console.error('❌ FRONTEND: Error deleting existing document:', error);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Failed to delete existing document: ${errorMessage}`);
            
            // Close modal even if deletion failed
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
        }
    };

    const handleDuplicateCancel = () => {
        console.log('❌ FRONTEND: User cancelled upload due to duplicate');
        
        // Simply close the modal and reset state
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
    };

    // Download AI Notes document
    const downloadDocument = async (documentId, filename) => {
        try {
            const response = await axiosInstance.get(
                `/api/docuware/ai-notes-documents/download/${documentId}/`,
                { responseType: 'blob' }
            );
            
            // Extract filename from headers if available
            let downloadFilename = filename;
            const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            if (!downloadFilename || downloadFilename === `AI_Notes_Document_${documentId}`) {
                const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
                let extension = '';
                if (contentType.includes('pdf')) {
                    extension = '.pdf';
                } else if (contentType.includes('image')) {
                    extension = '.jpg';
                } else if (contentType.includes('document') || contentType.includes('word')) {
                    extension = '.doc';
                }
                downloadFilename = `AI_Notes_Document_${documentId}${extension}`;
            }
            
            // Create blob URL and trigger download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log(`Downloaded AI Notes document ${documentId} as "${downloadFilename}"`);
            
        } catch (error) {
            console.error('Error downloading AI Notes document:', error);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Failed to download document: ${errorMessage}`);
        }
    };

    // Delete AI Notes document
    const deleteDocument = async (documentId, filename) => {
        try {
            await axiosInstance.delete(`/api/docuware/ai-notes-documents/delete/${documentId}/`);
            
            // Refresh document list
            fetchDocuments();
            
            showSuccess(`Document "${filename}" deleted successfully`);
            
        } catch (error) {
            console.error('Error deleting AI Notes document:', error);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Failed to delete document: ${errorMessage}`);
        }
    };

    // AI Analysis functions
    const runAIAnalysis = async () => {
        // Check if profile is saved first
        if (isNewProfile || !profileData?.id) {
            showError('Please save the partner profile first before running AI analysis');
            return;
        }
        
        if (!userText.trim() && selectedFiles.length === 0) {
            showError('Please enter text or select files for analysis');
            return;
        }

        console.log('🚀 FRONTEND: Starting AI Analysis');
        console.log(`🚀 Partner ID: ${profileData?.id}`);
        console.log(`🚀 User text length: ${userText.length}`);
        console.log(`🚀 Selected files count: ${selectedFiles.length}`);

        setSubmittingAnalysis(true);
        clearAnalysisRun();

        try {
            const mappedFiles = selectedFiles.map(file => ({
                document_id: file.document_id,
                filename: file.filename,
                note_type: file.note_type
            }));
            
            const requestPayload = {
                user_text: userText,
                selected_files: mappedFiles
            };

            // Answers straight away with a queued run; the result arrives through
            // handleAnalysisFinished once the background service has finished.
            const response = await axiosInstance.post(
                `/api/crm/partners/${profileData.id}/ai-notes/analyze/`,
                requestPayload
            );
            startAnalysisRun(response.data);
        } catch (error) {
            console.error('❌ FRONTEND: Error starting AI analysis:', error);
            showError(getErrorMessage(error, 'Analysis could not be started. Please try again.'));
        } finally {
            setSubmittingAnalysis(false);
        }
    };

    // Delete individual analysis function
    const deleteAnalysis = async (analysisId, analysisTimestamp) => {
        if (!analysisId || !profileData?.id) {
            console.error('🗑️ FRONTEND: Invalid analysis or profile data');
            showError('Invalid analysis or profile data');
            return;
        }

        console.log(`🗑️ FRONTEND: Starting delete analysis ${analysisId} for partner ${profileData.id}`);
        console.log(`🗑️ FRONTEND: Analysis timestamp: ${analysisTimestamp}`);
        console.log(`🗑️ FRONTEND: Current history length before delete: ${analysisHistory.length}`);

        setDeletingAnalysis(analysisId);
        
        try {
            console.log(`🗑️ FRONTEND: Sending DELETE request...`);
            
            const response = await axiosInstance.delete(
                `/api/crm/partners/${profileData.id}/ai-notes/analysis/${analysisId}/delete/`
            );

            console.log('🗑️ FRONTEND: Delete response status:', response.status);
            console.log('🗑️ FRONTEND: Delete response data:', response.data);

            if (response.data.success) {
                console.log('✅ FRONTEND: Analysis delete request successful');
                console.log('✅ FRONTEND: Remaining analyses on server:', response.data.remaining_analyses);
                
                // Remove the analysis from local state immediately for better UX
                const filteredHistory = analysisHistory.filter(analysis => analysis.analysis_id !== analysisId);
                console.log(`🗑️ FRONTEND: Updating local state: ${analysisHistory.length} -> ${filteredHistory.length}`);
                setAnalysisHistory(filteredHistory);
                
                // Also refresh from server to ensure consistency
                setTimeout(() => {
                    console.log('🗑️ FRONTEND: Refreshing from server for consistency check...');
                    fetchAnalysisHistory();
                }, 1000); // Increased delay
                
                showSuccess(`Analysis from ${new Date(analysisTimestamp).toLocaleDateString()} deleted successfully`);
            } else {
                console.error('🗑️ FRONTEND: Delete failed - server returned error:', response.data);
                showError('Failed to delete analysis - server returned error');
            }
        } catch (error) {
            console.error('❌ FRONTEND: Error deleting analysis:', error);
            console.error('❌ FRONTEND: Error response:', error.response?.data);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Failed to delete analysis: ${errorMessage}`);
        } finally {
            setDeletingAnalysis(null);
        }
    };

    // Load more analyses function
    const loadMoreAnalyses = () => {
        setDisplayedAnalysisCount(prev => prev + 5);
    };

    // Function to get employee info by Django user ID (same as KYC)
    const getEmployeeInfoByUserId = async (djangoUserId) => {
        if (!djangoUserId) return null;
        
        // Check cache first
        if (employeeInfoCache[djangoUserId]) {
            return employeeInfoCache[djangoUserId];
        }
        
        try {
            // For current user, use their own info
            if (currentUserInfo && currentUserInfo.user_id === parseInt(djangoUserId)) {
                const employeeInfo = {
                    first_name: currentUserInfo.first_name,
                    last_name: currentUserInfo.last_name,
                    position: currentUserInfo.position,
                    department: currentUserInfo.department,
                    office: currentUserInfo.office,
                    employee_id: currentUserInfo.employee_id
                };
                
                // Cache the result
                setEmployeeInfoCache(prev => ({
                    ...prev,
                    [djangoUserId]: employeeInfo
                }));
                
                return employeeInfo;
            } else {
                // For other users, return placeholder
                const placeholderInfo = {
                    first_name: 'User',
                    last_name: `ID ${djangoUserId}`,
                    position: 'Unknown',
                    department: 'Unknown',
                    office: 'Unknown',
                    employee_id: djangoUserId
                };
                
                return placeholderInfo;
            }
        } catch (error) {
            console.error(`Error fetching employee info for user ID ${djangoUserId}:`, error);
            return null;
        }
    };

    // Extract document metadata for display (same pattern as KYC)
    const getDocumentInfo = (document) => {
        const fields = document.Fields || [];
        const getFieldValue = (fieldName) => {
            const field = fields.find(f => f.FieldName === fieldName);
            return field?.Item || '';
        };
        
        const subject = getFieldValue('SUBJECT');
        const originalFilename = subject && subject !== 'AI Notes Document' ? subject : null;
        const fallbackFilename = `ai_notes_document_${document.Id}.pdf`;
        const filename = originalFilename || fallbackFilename;
        
        const dateUploaded = getFieldValue('DATE_UPLOADED');
        const noteType = getFieldValue('NOTE_TYPE');
        const employeeId = getFieldValue('EMPLOYEE_ID');
        
        const nameRow = aiNotesDisplayNames?.[String(document.Id)] || null;
        const aiDisplayName = nameRow?.display_name || null;
        
        const docInfo = {
            filename,
            displayName: aiDisplayName || filename,
            nameRow,
            onNameRow: (updated) => setAiNotesDisplayNames((prev) => ({ ...(prev || {}), [String(document.Id)]: updated })),
            aiDisplayName,
            dateUploaded,
            noteType,
            fileSize: document.FileSize || 0,
            documentId: document.Id,
            employeeId
        };
        
        // Debug logging for document info extraction
        if (filename.toLowerCase().includes('.pdf')) {
            console.log('📄 PDF Document Info:', { 
                originalDoc: document, 
                extractedInfo: docInfo,
                subject: subject,
                allFields: fields.map(f => ({ name: f.FieldName, value: f.Item }))
            });
        }
        
        return docInfo;
    };

    // Format date for display (same as KYC)
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        
        try {
            let date;
            
            if (typeof dateString === 'string') {
                // Check for .NET JSON date format
                const netDateMatch = dateString.match(/\/Date\((\d+)\)\//);
                if (netDateMatch) {
                    const timestamp = parseInt(netDateMatch[1], 10);
                    date = new Date(timestamp);
                } else {
                    // Handle regular ISO format dates
                    const cleanDateString = dateString.replace(/Z+$/, 'Z').trim();
                    date = new Date(cleanDateString);
                }
            } else {
                date = new Date(dateString);
            }
            
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            console.error('Date formatting error:', error);
            return 'Invalid Date';
        }
    };

    // Format file size (same as KYC)
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getSentimentIndicator = (report) => {
        if (report.includes('Positive')) return { label: 'Positive', color: '#059669', icon: '😊' };
        if (report.includes('Negative')) return { label: 'Negative', color: '#dc2626', icon: '😟' };
        return { label: 'Neutral', color: '#d97706', icon: '😐' };
    };

    return (
        <div className="crm-tab-panel crm-tab-modern">
            <CRMTabHero
                tone="violet"
                icon={<TabIcons.Sparkle />}
                title="AI Notes & Intelligence"
                description="AI-assisted notes, document analysis, and historical analysis runs for this partner."
            />

            {/* AI Notes Document Management Section - Following KYC Pattern */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.FileText />}
                    iconTone="blue"
                    title="AI Notes Document Management"
                />
                
                {/* Control Buttons - Same as KYC */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '30px' 
                }}>
                    <div>
                        <label className="crm-input-label">AI Notes Documents (auto-loaded)</label>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            Documents are automatically loaded and grouped by note type for AI analysis
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            type="button"
                            onClick={() => setShowDocuments(!showDocuments)}
                            style={{
                                background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                            }}
                        >
                            {showDocuments ? 'Hide Documents' : 'View Documents'}
                        </button>
                        {showDocuments && (
                            <button
                                type="button"
                                onClick={fetchDocuments}
                                disabled={loadingDocuments}
                                style={{
                                    background: loadingDocuments ? '#6c757d' : 'linear-gradient(135deg, #5a7b35, #4a6b25)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: loadingDocuments ? 'not-allowed' : 'pointer',
                                    opacity: loadingDocuments ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    boxShadow: loadingDocuments ? 'none' : '0 2px 4px rgba(74, 107, 37, 0.15)'
                                }}
                            >
                                {loadingDocuments ? 'Refreshing...' : '↻ Refresh'}
                            </button>
                        )}
                        {/* Upload button - always visible but disabled when profile not saved */}
                        <div style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={() => setShowUploadModal(true)}
                                disabled={uploading || isNewProfile || !profileData?.id}
                                style={{
                                    background: (uploading || isNewProfile || !profileData?.id) ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: (uploading || isNewProfile || !profileData?.id) ? 'not-allowed' : 'pointer',
                                    opacity: (uploading || isNewProfile || !profileData?.id) ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    boxShadow: (uploading || isNewProfile || !profileData?.id) ? 'none' : '0 2px 4px rgba(245, 158, 11, 0.15)'
                                }}
                            >
                                {uploading ? 'Uploading...' : 'Upload AI Notes Document'}
                            </button>
                            {(isNewProfile || !profileData?.id) && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    right: '0',
                                    marginTop: '0.25rem',
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    fontStyle: 'italic',
                                    wordWrap: 'break-word',
                                    lineHeight: '1.2'
                                }}>
                                    Save profile first to upload documents
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Document Display - Same structure as KYC */}
                {showDocuments && (
                    <div className="ai-notes-documents-container" style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        backgroundColor: '#f9fafb',
                        marginBottom: '30px'
                    }}>
                        {loadingDocuments && (
                            <div style={{ 
                                padding: '2rem', 
                                textAlign: 'center',
                                color: '#6b7280'
                            }}>
                                Loading AI Notes documents...
                            </div>
                        )}

                        {documentError && (
                            <div style={{ 
                                padding: '1rem',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #ef4444',
                                borderRadius: '0.375rem',
                                color: '#dc2626',
                                margin: '1rem'
                            }}>
                                Error: {documentError}
                            </div>
                        )}

                        {!loadingDocuments && !documentError && Object.keys(groupedDocuments).length === 0 && (
                            <div style={{ 
                                padding: '2rem', 
                                textAlign: 'center',
                                color: '#6b7280'
                            }}>
                                No AI Notes documents found for this partner
                            </div>
                        )}

                        {!loadingDocuments && !documentError && Object.keys(groupedDocuments).map(noteType => (
                            <AINotesDocumentGroup
                                key={noteType}
                                noteType={noteType}
                                documents={groupedDocuments[noteType]}
                                onDownload={downloadDocument}
                                onDelete={(documentId, filename) => {
                                    setDocumentToDelete({ id: documentId, filename });
                                    setShowDeleteConfirmation(true);
                                }}
                                onSelect={(file, isSelected) => {
                                    console.log('📎 File selection changed:', { file, isSelected });
                                    if (isSelected) {
                                        setSelectedFiles(prev => {
                                            const newSelection = [...prev, file];
                                            console.log('✅ Adding file, new selection:', newSelection);
                                            return newSelection;
                                        });
                                    } else {
                                        setSelectedFiles(prev => {
                                            const newSelection = prev.filter(f => f.document_id !== file.document_id);
                                            console.log('❌ Removing file, new selection:', newSelection);
                                            return newSelection;
                                        });
                                    }
                                }}
                                selectedFiles={selectedFiles}
                                getDocumentInfo={getDocumentInfo}
                                formatDate={formatDate}
                                formatFileSize={formatFileSize}
                                getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* AI Analysis Section */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.Sparkle />}
                    iconTone="violet"
                    title="AI Analysis"
                />
                
                {/* Info text */}
                <div style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '0.5rem',
                    marginBottom: '1.5rem',
                    fontSize: '0.875rem',
                    color: '#1e40af',
                    lineHeight: '1.5'
                }}>
                    <strong>How to use AI Analysis:</strong> Select files from the document list above and/or enter manual notes below. The AI will analyze both your text input and selected documents to provide comprehensive insights about this partner.
                </div>
                
                {/* Text input */}
                <div className="crm-input-group" style={{ marginBottom: '10px' }}>
                    <label className="crm-input-label">Manual Notes Input</label>
                    <textarea
                        className="crm-textarea"
                        rows={6}
                        value={userText}
                        onChange={(e) => setUserText(e.target.value)}
                        placeholder="Enter notes, observations, meeting minutes, or any information about this partner for AI analysis..."
                        disabled={analyzing}
                    />
                </div>

                {/* Selected files summary */}
                {selectedFiles.length > 0 && (
                    <div className="crm-input-group">
                        <label className="crm-input-label">Selected Files ({selectedFiles.length})</label>
                        <div className="selected-files-summary" style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            padding: '0.75rem',
                            backgroundColor: '#f9fafb'
                        }}>
                            {selectedFiles.map(file => (
                                <div key={file.document_id} className="selected-file-item" style={{
                                    fontSize: '0.875rem',
                                    color: '#374151',
                                    marginBottom: '0.25rem'
                                }}>
                                    {file.filename} ({file.note_type})
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Analysis button with progress */}
                <div className="crm-input-group">
                    <button
                        type="button"
                        onClick={runAIAnalysis}
                        disabled={analyzing || (!userText.trim() && selectedFiles.length === 0) || isNewProfile || !profileData?.id}
                        className="crm-btn-primary"
                        style={{ 
                            width: '200px',
                            background: (analyzing || (!userText.trim() && selectedFiles.length === 0) || isNewProfile || !profileData?.id) 
                                ? '#6c757d' 
                                : undefined,
                            cursor: (analyzing || (!userText.trim() && selectedFiles.length === 0) || isNewProfile || !profileData?.id) ? 'not-allowed' : 'pointer',
                            opacity: (analyzing || (!userText.trim() && selectedFiles.length === 0) || isNewProfile || !profileData?.id) ? 0.6 : 1
                        }}
                    >
                        {analyzing ? 'Analyzing...' : 'Run AI Analysis'}
                    </button>

                    <DocumentReadingRunPanel
                        run={analysisRun}
                        activeText="Reading the chosen documents and analysing them. Scanned documents can take several minutes; this updates by itself."
                        onDismiss={clearAnalysisRun}
                    />
                    
                    {/* Helper text for disabled state */}
                    {(isNewProfile || !profileData?.id) && (
                        <div style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            fontStyle: 'italic',
                            marginTop: '0.5rem'
                        }}>
                            Save profile first to run AI analysis
                        </div>
                    )}
                </div>
            </div>

            {/* Analysis Results History */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.History />}
                    iconTone="indigo"
                    title="Analysis Results History"
                    count={analysisHistory.length}
                    countLabel={analysisHistory.length === 1 ? 'analysis' : 'analyses'}
                />
                
                {analysisHistory.length === 0 ? (
                    <div className="no-analysis-message" style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: '#6b7280'
                    }}>
                        No analyses performed yet. Enter text or select documents and run AI analysis.
                    </div>
                ) : (
                    <div className="analysis-history">
                        {analysisHistory.slice(0, displayedAnalysisCount).map((analysis, index) => (
                            <AINotesAnalysisBlock
                                key={analysis.analysis_id || index}
                                analysis={analysis}
                                isLatest={index === 0}
                                onDelete={deleteAnalysis}
                                deletingAnalysis={deletingAnalysis}
                            />
                        ))}
                        
                        {/* Load More Button */}
                        {analysisHistory.length > displayedAnalysisCount && (
                            <div style={{ 
                                textAlign: 'center', 
                                marginTop: '1rem',
                                paddingTop: '1rem',
                                borderTop: '1px solid #e5e7eb'
                            }}>
                                <button
                                    type="button"
                                    onClick={loadMoreAnalyses}
                                    style={{
                                        background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.75rem 1.5rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 2px 4px rgba(107, 114, 128, 0.15)'
                                    }}
                                    onMouseOver={(e) => {
                                        e.target.style.background = 'linear-gradient(135deg, #4b5563, #374151)';
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 4px 8px rgba(107, 114, 128, 0.25)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.target.style.background = 'linear-gradient(135deg, #6b7280, #4b5563)';
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 2px 4px rgba(107, 114, 128, 0.15)';
                                    }}
                                >
                                    Load More Analyses ({analysisHistory.length - displayedAnalysisCount} remaining)
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Upload Modal */}
            <AINotesUploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                noteTypes={AI_NOTES_DOCUMENT_TYPES}
                onDocumentSelect={handleFileUpload}
                uploading={uploading}
                profileId={profileData?.id}
                partnerName={profileData?.legal_entity_name}
            />

            {/* Duplicate Detection Modal */}
            {showDuplicateModal && duplicateFileInfo && (
                <div className="confirmation-popup-overlay">
                    <div className="confirmation-popup">
                        <div className="confirmation-popup-message">
                            <h3 style={{ 
                                margin: '0 0 1rem 0', 
                                color: '#374151',
                                fontSize: '1.25rem',
                                fontWeight: '600'
                            }}>
                                Duplicate File Detected
                            </h3>
                            <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
                                A file with the name "<strong>{duplicateFileInfo.file.name}</strong>" already exists in this partner's documents.
                            </p>
                            <p style={{ margin: '0', color: '#374151', fontSize: '0.95rem' }}>
                                What would you like to do?
                            </p>
                        </div>
                        <div className="confirmation-popup-buttons" style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            gap: '0.75rem',
                            marginTop: '1.5rem',
                            flexWrap: 'wrap' 
                        }}>
                            <button 
                                onClick={handleDuplicateKeepBoth}
                                disabled={uploading}
                                style={{
                                    background: uploading ? '#6c757d' : 'linear-gradient(135deg, #10b981, #059669)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '0.6rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    minWidth: '90px'
                                }}
                            >
                                Keep Both
                            </button>
                            <button 
                                onClick={handleDuplicateReplace}
                                disabled={uploading}
                                style={{
                                    background: uploading ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '0.6rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    minWidth: '90px'
                                }}
                            >
                                Replace
                            </button>
                            <button 
                                onClick={handleDuplicateCancel}
                                disabled={uploading}
                                style={{
                                    background: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '0.6rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    minWidth: '90px'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirmation && documentToDelete && (
                <ConfirmationPopup
                    message={`Are you sure you want to delete "${documentToDelete.filename}"? This action cannot be undone.`}
                    onConfirm={() => {
                        deleteDocument(documentToDelete.id, documentToDelete.filename);
                        setShowDeleteConfirmation(false);
                        setDocumentToDelete(null);
                    }}
                    onCancel={() => {
                        setShowDeleteConfirmation(false);
                        setDocumentToDelete(null);
                    }}
                />
            )}
        </div>
    );
};

// Component to display AI Notes document group following KYC pattern
const AINotesDocumentGroup = ({ 
    noteType, 
    documents, 
    onDownload, 
    onDelete, 
    onSelect,
    selectedFiles,
    getDocumentInfo, 
    formatDate, 
    formatFileSize,
    getEmployeeInfoByUserId
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    
    return (
        <div className="ai-notes-document-group">
            <div 
                className="ai-notes-group-header"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ 
                    cursor: 'pointer',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                    borderRadius: '6px',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)',
                    transition: 'all 0.2s ease'
                }}
            >
                <h4 style={{ margin: 0, color: 'white', fontWeight: '600', fontSize: '1rem' }}>
                    {noteType} Documents ({documents.length})
                </h4>
                <span style={{ 
                    fontSize: '1rem', 
                    color: 'white',
                    fontWeight: '600',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                }}>
                    ⌄
                </span>
            </div>
            
            {isExpanded && (
                <div className="ai-notes-group-documents">
                    {documents.map(document => (
                        <AINotesDocumentItem
                            key={document.Id}
                            document={document}
                            onDownload={onDownload}
                            onDelete={onDelete}
                            onSelect={onSelect}
                            selectedFiles={selectedFiles}
                            getDocumentInfo={getDocumentInfo}
                            formatDate={formatDate}
                            formatFileSize={formatFileSize}
                            getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Individual AI Notes document item following KYC pattern with selection checkbox
const AINotesDocumentItem = ({ 
    document, 
    onDownload, 
    onDelete, 
    onSelect,
    selectedFiles,
    getDocumentInfo, 
    formatDate, 
    formatFileSize, 
    getEmployeeInfoByUserId
}) => {
    const [employeeInfo, setEmployeeInfo] = useState(null);
    const [loadingEmployee, setLoadingEmployee] = useState(false);
    
    const docInfo = getDocumentInfo(document);
    
    // Create file object for selection
    const fileForSelection = {
        document_id: docInfo.documentId,
        filename: docInfo.filename,
        note_type: docInfo.noteType
    };
    
    const isSelected = selectedFiles.some(f => f.document_id === docInfo.documentId);
    
    // Fetch employee info when component mounts
    useEffect(() => {
        const fetchEmployeeInfo = async () => {
            if (!docInfo.employeeId) return;
            
            setLoadingEmployee(true);
            try {
                const empInfo = await getEmployeeInfoByUserId(docInfo.employeeId);
                setEmployeeInfo(empInfo);
            } catch (error) {
                console.error('Error fetching employee info for document:', error);
            } finally {
                setLoadingEmployee(false);
            }
        };
        
        fetchEmployeeInfo();
    }, [docInfo.employeeId, getEmployeeInfoByUserId]);
    
    const handleSelectionChange = (e) => {
        onSelect(fileForSelection, e.target.checked);
    };
    
    return (
        <div 
            className={`crm-ai-notes-document-item ${isSelected ? 'selected' : ''}`}
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                marginBottom: '0.5rem',
                backgroundColor: '#ffffff',
                borderRadius: '6px',
                border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb'
            }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1 }}>
                {/* Selection checkbox */}
                <input
                    type="checkbox"
                    className="crm-ai-notes-file-checkbox"
                    checked={isSelected}
                    onChange={handleSelectionChange}
                />
                
                <div style={{ flex: 1 }}>
                    <div style={{ 
                        fontWeight: 'bold', 
                        color: '#495057',
                        marginBottom: '0.25rem',
                        fontSize: '0.95rem'
                    }} title={docInfo.displayName || docInfo.filename}>
                        {docInfo.displayName || docInfo.filename}
                    </div>
                    <SuggestedName row={docInfo.nameRow} onChanged={docInfo.onNameRow} />
                    {docInfo.aiDisplayName && docInfo.aiDisplayName !== docInfo.filename && (
                        <div style={{
                            fontSize: '0.75rem',
                            color: '#888',
                            marginBottom: '0.25rem',
                            wordBreak: 'break-all'
                        }} title={docInfo.filename}>
                            {docInfo.filename}
                        </div>
                    )}

                    <div style={{
                        fontSize: '0.85rem', 
                        color: '#6c757d',
                        marginBottom: '0.25rem'
                    }}>
                        Uploaded: {formatDate(docInfo.dateUploaded)} | Size: {formatFileSize(docInfo.fileSize)}
                    </div>
                    
                    {/* Employee Information Section */}
                    <div style={{ 
                        fontSize: '0.85rem', 
                        color: '#495057',
                        marginTop: '0.5rem',
                        padding: '0.4rem 0.6rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6'
                    }}>
                        <strong>Uploaded by:</strong>
                        {loadingEmployee ? (
                            <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>Loading...</span>
                        ) : employeeInfo ? (
                            <div style={{ marginTop: '0.25rem' }}>
                                <div><strong>{employeeInfo.first_name} {employeeInfo.last_name}</strong></div>
                                {employeeInfo.position && (
                                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                        {employeeInfo.position}
                                        {employeeInfo.department && ` • ${employeeInfo.department}`}
                                        {employeeInfo.office && ` • ${employeeInfo.office}`}
                                    </div>
                                )}
                                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                    Employee ID: {employeeInfo.employee_id}
                                </div>
                            </div>
                        ) : (
                            <span style={{ marginLeft: '0.5rem', fontStyle: 'italic', color: '#6c757d' }}>
                                Employee info not available
                            </span>
                        )}
                    </div>
                    
                    <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#6c757d',
                        marginTop: '0.25rem'
                    }}>
                        Document ID: {docInfo.documentId}
                    </div>
                </div>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                <button
                    onClick={() => onDownload(docInfo.documentId, docInfo.filename)}
                    style={{
                        background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '80px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                    }}
                >
                    Download
                </button>
                <button
                    onClick={() => onDelete(docInfo.documentId, docInfo.filename)}
                    style={{
                        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '80px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(220, 38, 38, 0.15)'
                    }}
                >
                    Delete
                </button>
            </div>
        </div>
    );
};

// Simple Analysis Block component - will be enhanced later with full document-processing integration
const AINotesAnalysisBlock = ({ analysis, isLatest, onDelete, deletingAnalysis }) => {
    const [isExpanded, setIsExpanded] = useState(isLatest);

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDelete = () => {
        if (onDelete && analysis.analysis_id) {
            onDelete(analysis.analysis_id, analysis.timestamp);
        }
    };

    const isDeleting = deletingAnalysis === analysis.analysis_id;

    return (
        <div className={`analysis-block ${isLatest ? 'latest' : ''}`} style={{
            border: isLatest ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: isLatest ? '#f0f9ff' : '#ffffff'
        }}>
            <div 
                className="analysis-header"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ cursor: 'pointer' }}
            >
                <div className="analysis-title" style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                }}>
                    <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', flex: 1 }}>
                        AI Analysis - {formatTimestamp(analysis.timestamp)}
                        {isLatest && <span style={{ 
                            marginLeft: '0.5rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                        }}>Latest</span>}
                    </h3>
                </div>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                }}>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {analysis.selected_files?.length || 0} documents, 
                        {analysis.user_input_text ? ' manual notes' : ' no manual notes'}
                    </span>
                    <span style={{ 
                        fontSize: '1rem', 
                        color: '#6b7280',
                        fontWeight: '600',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ⌄
                    </span>
                </div>
            </div>

            {isExpanded && (
                <div className="analysis-content" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    {/* User input section */}
                    {analysis.user_input_text && (
                        <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>User Input</h4>
                            <div style={{
                                padding: '0.75rem',
                                backgroundColor: '#f9fafb',
                                borderRadius: '0.375rem',
                                border: '1px solid #e5e7eb',
                                fontSize: '0.875rem',
                                color: '#374151'
                            }}>
                                {analysis.user_input_text}
                            </div>
                        </div>
                    )}

                    {/* Documents processed */}
                    {analysis.selected_files && analysis.selected_files.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>Documents Analyzed</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                {analysis.selected_files.map((file, index) => (
                                    <div key={index} style={{
                                        padding: '0.5rem',
                                        backgroundColor: '#f9fafb',
                                        borderRadius: '0.375rem',
                                        border: '1px solid #e5e7eb',
                                        fontSize: '0.875rem'
                                    }}>
                                        <span style={{ fontWeight: '500' }}>{file.filename}</span>
                                        {file.note_type && (
                                            <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>({file.note_type})</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Analysis results */}
                    <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>Analysis Results</h4>
                        {analysis.analysis_results ? (
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                {/* Action Items */}
                                {analysis.analysis_results.action_items && analysis.analysis_results.action_items.length > 0 && (
                                    <div style={{
                                        padding: '1rem',
                                        backgroundColor: '#f0f9ff',
                                        borderRadius: '0.5rem',
                                        border: '1px solid #3b82f6'
                                    }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', color: '#1e40af', fontWeight: '600' }}>
                                            Action Items
                                        </h5>
                                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                            {analysis.analysis_results.action_items.map((item, index) => (
                                                <li key={index} style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Discussion Points */}
                                {analysis.analysis_results.discussion_points && analysis.analysis_results.discussion_points.length > 0 && (
                                    <div style={{
                                        padding: '1rem',
                                        backgroundColor: '#f0fdf4',
                                        borderRadius: '0.5rem',
                                        border: '1px solid #22c55e'
                                    }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', color: '#15803d', fontWeight: '600' }}>
                                            Discussion Points
                                        </h5>
                                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                            {analysis.analysis_results.discussion_points.map((point, index) => (
                                                <li key={index} style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                                                    {point}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Upcoming Meetings */}
                                {analysis.analysis_results.upcoming_meetings && analysis.analysis_results.upcoming_meetings.length > 0 && (
                                    <div style={{
                                        padding: '1rem',
                                        backgroundColor: '#fffbeb',
                                        borderRadius: '0.5rem',
                                        border: '1px solid #f59e0b'
                                    }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', color: '#d97706', fontWeight: '600' }}>
                                            Upcoming Meetings
                                        </h5>
                                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                            {analysis.analysis_results.upcoming_meetings.map((meeting, index) => (
                                                <li key={index} style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                                                    {meeting}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Partner Info */}
                                {analysis.analysis_results.partner_info && analysis.analysis_results.partner_info.trim() && (
                                    <div style={{
                                        padding: '1rem',
                                        backgroundColor: '#fafafa',
                                        borderRadius: '0.5rem',
                                        border: '1px solid #6b7280'
                                    }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontWeight: '600' }}>
                                            Partner Information
                                        </h5>
                                        <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: '1.5' }}>
                                            {analysis.analysis_results.partner_info}
                                        </p>
                                    </div>
                                )}

                                {/* Partner Sentiment */}
                                {analysis.analysis_results.partner_sentiment && analysis.analysis_results.partner_sentiment.trim() && (
                                    <div style={{
                                        padding: '1rem',
                                        backgroundColor: '#f3e8ff',
                                        borderRadius: '0.5rem',
                                        border: '1px solid #8b5cf6'
                                    }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', color: '#7c3aed', fontWeight: '600' }}>
                                            Partner Sentiment
                                        </h5>
                                        <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: '1.5' }}>
                                            {analysis.analysis_results.partner_sentiment}
                                        </p>
                                    </div>
                                )}

                                {/* Red Flags */}
                                {analysis.analysis_results.red_flags && analysis.analysis_results.red_flags.length > 0 && (
                                    <div style={{
                                        padding: '1rem',
                                        backgroundColor: '#fef2f2',
                                        borderRadius: '0.5rem',
                                        border: '1px solid #ef4444'
                                    }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', color: '#dc2626', fontWeight: '600' }}>
                                            Red Flags
                                        </h5>
                                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                            {analysis.analysis_results.red_flags.map((flag, index) => (
                                                <li key={index} style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                                                    {flag}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Business Opportunities */}
                                {analysis.analysis_results.business_opportunities && analysis.analysis_results.business_opportunities.length > 0 && (
                                    <div style={{
                                        padding: '1rem',
                                        backgroundColor: '#ecfdf5',
                                        borderRadius: '0.5rem',
                                        border: '1px solid #10b981'
                                    }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', color: '#059669', fontWeight: '600' }}>
                                            Business Opportunities
                                        </h5>
                                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                            {analysis.analysis_results.business_opportunities.map((opportunity, index) => (
                                                <li key={index} style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                                                    {opportunity}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Other Info */}
                                {analysis.analysis_results.other_info && analysis.analysis_results.other_info.length > 0 && (
                                    <div style={{
                                        padding: '1rem',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '0.5rem',
                                        border: '1px solid #94a3b8'
                                    }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: '600' }}>
                                            Other Information
                                        </h5>
                                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                            {(() => {
                                                // Handle both string and array cases for other_info
                                                const otherInfo = analysis.analysis_results.other_info;
                                                if (Array.isArray(otherInfo)) {
                                                    return otherInfo.map((info, index) => (
                                                        <li key={index} style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                                                            {info}
                                                        </li>
                                                    ));
                                                } else if (typeof otherInfo === 'string' && otherInfo.trim()) {
                                                    // If it's a string, wrap it in a single list item
                                                    return (
                                                        <li style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                                                            {otherInfo}
                                                        </li>
                                                    );
                                                } else {
                                                    // Fallback for empty or invalid data
                                                    return (
                                                        <li style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontStyle: 'italic', color: '#64748b' }}>
                                                            No additional information available
                                                        </li>
                                                    );
                                                }
                                            })()}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{
                                padding: '1rem',
                                backgroundColor: '#fef2f2',
                                borderRadius: '0.375rem',
                                border: '1px solid #ef4444',
                                fontSize: '0.875rem',
                                color: '#dc2626'
                            }}>
                                No analysis results available. There may have been an error during processing.
                            </div>
                        )}
                    </div>
                    {/* Delete Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent expanding/collapsing when clicking delete
                            handleDelete();
                        }}
                        disabled={isDeleting}
                        style={{
                            background: isDeleting ? '#6b7280' : 'linear-gradient(135deg, #dc2626, #b91c1c)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                            minWidth: '80px',
                            transition: 'all 0.2s',
                            boxShadow: isDeleting ? 'none' : '0 2px 4px rgba(220, 38, 38, 0.15)',
                            opacity: isDeleting ? 0.6 : 1,
                            marginTop: '1rem'
                        }}
                        title="Delete this analysis"
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CRMAINotesTab; 